import type { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import type { ColumnConfig } from '../types/panelConfig';
import {
  SparklineCellRenderer,
  ProgressBarCellRenderer,
  StatusBadgeCellRenderer,
  DrillDownCellRenderer,
  buildCellClassRules,
} from './cellRenderers';

const currencyFormatter = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
const numberFormatter = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const percentFormatter = new Intl.NumberFormat('tr-TR', { style: 'percent', minimumFractionDigits: 2 });

// Backend metadata emits .NET/Excel tokens (yyyy, dd); dayjs expects YYYY/DD.
export function toDayjsFormat(dotnetFormat: string): string {
  return dotnetFormat.replace(/yyyy/g, 'YYYY').replace(/yy/g, 'YY').replace(/dd/g, 'DD').replace(/\bd\b/g, 'D');
}

export function formatValue(col: ColumnConfig, value: unknown): string {
  if (value == null) return '';
  if (col.cellRenderer === 'currency' || col.type === 'currency') return currencyFormatter.format(Number(value));
  if (col.cellRenderer === 'percentage' || col.type === 'percentage') return percentFormatter.format(Number(value) / 100);
  if (col.type === 'number') return numberFormatter.format(Number(value));
  if (col.type === 'date' && col.format) return dayjs(String(value)).format(toDayjsFormat(col.format));
  return String(value);
}

function getCellValueFormatter(col: ColumnConfig) {
  if (col.type === 'currency' || col.cellRenderer === 'currency' ||
      col.type === 'percentage' || col.cellRenderer === 'percentage' ||
      col.type === 'number') {
    return (params: { value: unknown }) => formatValue(col, params.value);
  }
  if (col.type === 'date' && col.format) {
    return (params: { value: unknown }) => formatValue(col, params.value);
  }
  return undefined;
}

export function buildAgGridColumns(
  columns: ColumnConfig[],
  onDrillDown?: (reportCode: string, paramValue: string | number) => void,
): ColDef[] {
  return columns.map((col) => {
    const isNumeric = col.type === 'currency' || col.type === 'number' || col.type === 'percentage';

    const base: ColDef = {
      field: col.field,
      headerName: col.header,
      width: col.width || undefined,
      pinned: col.pinned ? ('left' as const) : undefined,
      filter: col.filterable,
      sortable: true,
      resizable: true,
      cellClass: isNumeric ? 'tabular ag-right' : undefined,
      cellStyle: isNumeric
        ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: "'tnum'" }
        : undefined,
      headerClass: isNumeric ? 'ag-right-header' : undefined,
    };

    // Structured predicate → AG Grid function map (NO dynamic eval)
    if (col.cellClassRules) {
      base.cellClassRules = buildCellClassRules(col.cellClassRules);
    }

    switch (col.cellRenderer) {
      case 'sparkline': {
        const cfg = col.sparkline ?? { dataField: col.field, type: 'line' as const };
        return {
          ...base,
          valueGetter: (p: { data: Record<string, unknown> }) => {
            const v = p.data?.[cfg.dataField];
            return Array.isArray(v) ? v : null;
          },
          cellRenderer: SparklineCellRenderer,
          cellRendererParams: { type: cfg.type, color: cfg.color },
          sortable: false,
          filter: false,
        };
      }
      case 'progressBar': {
        const params = (col.cellRendererParams ?? {}) as { maxField?: string; color?: string };
        return {
          ...base,
          cellRenderer: ProgressBarCellRenderer,
          cellRendererParams: (p: { data: Record<string, unknown>; value: unknown }) => ({
            value: p.value,
            max: params.maxField ? p.data?.[params.maxField] : undefined,
            color: params.color,
          }),
        };
      }
      case 'statusBadge': {
        const params = (col.cellRendererParams ?? {}) as { map?: Record<string, { label: string; color: string }> };
        return {
          ...base,
          cellRenderer: StatusBadgeCellRenderer,
          cellRendererParams: { map: params.map ?? {} },
        };
      }
      case 'drillDown': {
        const params = (col.cellRendererParams ?? {}) as { targetReportCode?: string };
        return {
          ...base,
          cellRenderer: DrillDownCellRenderer,
          cellRendererParams: {
            targetReportCode: params.targetReportCode ?? '',
            onDrillDown: onDrillDown ?? (() => {}),
          },
        };
      }
      default: {
        const _exhaustive: 'currency' | 'percentage' | null = col.cellRenderer;
        void _exhaustive;
        return { ...base, valueFormatter: getCellValueFormatter(col), aggFunc: col.agg || undefined };
      }
    }
  });
}
